import * as React from "react";

import { LocalizationProvider } from "@mui/x-date-pickers";
import ptBrLocale from "date-fns/locale/pt-BR";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { CustomLabel } from "../input/CustomLabel";
import { InputErrorMessage } from "../input/InputErrorMessage";
import { Controller, Control, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { DirecoesCalendario } from "types/inputs/DatePicker/DirecoesCalendario";

type CustomTimePickerWithoutValidationProps = {
  usarValidacaoParaFormulario: false;
};

type CustomTimePickerWithValidationProps = {
  usarValidacaoParaFormulario: true;
  /**Nome do atributo que será usado no formulario para realizar as validacoes*/
  name: string;
  register: UseFormRegister<any>;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
};

type CustomTimePickerProps = {
  label?: string;
  disabled?: boolean;
  disabledFuture?: boolean;
  disabledPast?: boolean;
  onChange?: (dataSelecionada: Date | null) => void;
  defaultValue?: Date;
  popperDirection?: DirecoesCalendario;
} & (CustomTimePickerWithoutValidationProps | CustomTimePickerWithValidationProps);

export const CustomTimePicker: React.FC<CustomTimePickerProps> = (props) => {
  const renderTimePickerComValidacao = (): React.ReactNode => {
    if (props.usarValidacaoParaFormulario) {
      return (
        <Controller
          name={props.name}
          control={props.control}
          defaultValue={props.defaultValue}
          render={({ field, fieldState }) => (
            <>
              {props.label && (
                <CustomLabel possuiErro={fieldState.error != null}>{props.label}</CustomLabel>
              )}
              <TimePicker
                {...props.register(props.name)}
                {...field}
                disabled={props.disabled}
                disableFuture={props.disabledFuture}
                disablePast={props.disabledPast}
                value={field.value || null}
                slotProps={{
                  popper: {
                    placement: props.popperDirection || "bottom-start",
                  },
                  textField: {
                    variant: "outlined",
                    color: "info",
                    size: "small",
                    label: "",
                    error: fieldState.error != null,
                  },
                }}
                onChange={(dataSelecionada: Date | null) => {
                  field.onChange(dataSelecionada);
                  props.setValue(props.name, dataSelecionada);
                  props.onChange && props.onChange(dataSelecionada);
                }}
              />
              {fieldState.error && (
                <InputErrorMessage>{fieldState.error.message}</InputErrorMessage>
              )}
            </>
          )}
        />
      );
    }
  };

  const renderTimePickerSemValidacao = (): React.ReactNode => {
    if (!props.usarValidacaoParaFormulario) {
      return (
        <>
          {props.label && <CustomLabel possuiErro={false}>{props.label}</CustomLabel>}
          <TimePicker
            disabled={props.disabled}
            disableFuture={props.disabledFuture}
            disablePast={props.disabledPast}
            defaultValue={props.defaultValue}
            slotProps={{
              popper: {
                placement: props.popperDirection || "bottom-start",
              },
              textField: {
                variant: "outlined",
                color: "info",
                size: "small",
                label: "",
              },
            }}
            onChange={(dataSelecionada: Date | null) =>
              props.onChange && props.onChange(dataSelecionada)
            }
          />
        </>
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBrLocale}>
      {props.usarValidacaoParaFormulario
        ? renderTimePickerComValidacao()
        : renderTimePickerSemValidacao()}
    </LocalizationProvider>
  );
};
